"use client";
import React, { useState, useEffect } from "react";
import { Typography, Container, Box, Paper, Link } from "@mui/material";
import { Plus_Jakarta_Sans, Inter } from "next/font/google";
import { useTranslation } from "react-i18next"; // ← ADD

const PlusJakartaSans = Plus_Jakarta_Sans({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
});

const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
});

const TableOfContents = ({ isArabic }) => {
  // ← RECEIVE isArabic
  const { t } = useTranslation(); // ← ADD
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { threshold: 0.5 },
    );
    document
      .querySelectorAll("h1, h2, h3, h4")
      .forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: "fixed",
        right: isArabic ? "auto" : { xs: "5%", sm: "9.5%" }, // ← RTL flip
        left: isArabic ? { xs: "5%", sm: "9.5%" } : "auto", // ← RTL flip
        top: { xs: "50%", sm: "66.6%" },
        transform: "translateY(-50%)",
        display: { xs: "none", sm: "none", md: "none", xl: "none" },
        width: "280px",
        p: 2,
        maxHeight: "calc(80vh - 20%)",
        overflowY: "auto",
        scrollBehavior: "smooth",
        textAlign: isArabic ? "right" : "left", // ← ADD
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontFamily: PlusJakartaSans.style.fontFamily, fontWeight: "700" }}
      >
        {t("articleFiveContent.toc.title")} {/* ← REPLACE */}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {[
          { id: "overview", key: "overview", indent: 0 },
          { id: "what-is-bad-hire", key: "whatIsBadHire", indent: 0 },
          { id: "devastating-cost", key: "devastatingCost", indent: 0 },
          { id: "lower-productivity", key: "lowerProductivity", indent: 1 },
          { id: "team-morale", key: "teamMorale", indent: 1 },
          { id: "lost-clients", key: "lostClients", indent: 1 },
          { id: "damaged-reputation", key: "damagedReputation", indent: 1 },
          { id: "decreased-teamwork", key: "decreasedTeamwork", indent: 1 },
          { id: "lost-time", key: "lostTime", indent: 1 },
          { id: "recruitment-costs", key: "recruitmentCosts", indent: 1 },
          {
            id: "prevention-strategies",
            key: "preventionStrategies",
            indent: 0,
          },
          { id: "assess-culture", key: "assessCulture", indent: 1 },
          { id: "hiring-strategy", key: "hiringStrategy", indent: 1 },
          { id: "skills-tests", key: "skillsTests", indent: 1 },
          { id: "examine-roles", key: "examineRoles", indent: 1 },
          { id: "hiring-tools", key: "hiringTools", indent: 1 },
          { id: "conclusion", key: "conclusion", indent: 0 },
        ].map(({ id, key, indent }) => (
          <Typography
            key={id}
            onClick={() => scrollToSection(id)}
            sx={{
              cursor: "pointer",
              pl: isArabic ? 0 : indent * 2, // ← RTL flip
              pr: isArabic ? indent * 2 : 0, // ← RTL flip
              color: activeId === id ? "primary.main" : "#4B5963",
              fontFamily: inter.style.fontFamily,
              fontSize: "14px",
              "&:hover": { color: "primary.main" },
            }}
          >
            {t(`articleFiveContent.toc.${key}`)} {/* ← REPLACE */}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
};

const ArticleSixContent = () => {
  const { t, i18n } = useTranslation(); // ← ADD
  const isArabic = i18n.language === "ar"; // ← ADD

  const bodyStyle = {
    fontFamily: inter.style.fontFamily,
    fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
    color: "#4B5963",
    fontWeight: "400",
  };

  return (
    <Container maxWidth="xl" sx={{ direction: isArabic ? "rtl" : "ltr" }}>
      {" "}
      {/* ← ADD */}
      <TableOfContents isArabic={isArabic} /> {/* ← PASS isArabic */}
      <Paper elevation={3} sx={{ my: 4, borer: "none", boxShadow: "none" }}>
        {/* Overview */}
        <Typography
          id="overview"
          variant="h4"
          component="h1"
          gutterBottom
          sx={{
            fontFamily: PlusJakartaSans.style.fontFamily,
            fontWeight: "700",
            fontSize: { xs: "24px", sm: "28px", md: "34px" },
          }}
        >
          {t("articleFiveContent.overview.heading")}
        </Typography>
        <Typography sx={{ mb: ".75%", ...bodyStyle }}>
          {t("articleFiveContent.overview.p1")}
        </Typography>
        <Typography sx={{ mb: ".75%", ...bodyStyle }}>
          {t("articleFiveContent.overview.p2")}
        </Typography>
        <Typography sx={{ mb: ".75%", ...bodyStyle }}>
          {t("articleFiveContent.overview.p3")}
        </Typography>
        <Typography sx={{ mb: ".75%", ...bodyStyle }}>
          {t("articleFiveContent.overview.p4")}
        </Typography>
        <Typography sx={{ ...bodyStyle }}>
          {t("articleFiveContent.overview.p5")}
        </Typography>
      </Paper>
    </Container>
  );
};

export default ArticleSixContent;
